import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from './ui/carousel';
import {
  Calendar,
  MapPin,
  Users,
  ArrowLeft,
  ExternalLink,
  User,
  CheckCircle,
  XCircle,
  Map as MapIcon,
  Ban,
  Pencil,
  UserMinus,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import VerifiedBadge from './VerifiedBadge';
import EventChat from './EventChat';
import {
  assetUrl,
  cancelEvent,
  getEvent,
  getEventParticipants,
  getTerms,
  removeEventParticipant,
  rsvp,
  updateMe,
} from '../apis/ride-24';

const apiError = (error, fallback) =>
  error.response?.data?.detail || error.response?.data?.message || fallback;

const EventDetails = ({ user, onUserRefresh }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [userRsvp, setUserRsvp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [pendingRsvpStatus, setPendingRsvpStatus] = useState(null);
  const [terms, setTerms] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [removingParticipant, setRemovingParticipant] = useState(null);
  const [removalWorking, setRemovalWorking] = useState(false);
  const [genderDialogOpen, setGenderDialogOpen] = useState(false);
  const [savingGender, setSavingGender] = useState(false);

  const isOrganiser = event && event.organizer_id === user.id;
  const isCancelled = event?.status === 'cancelled';
  const currentUserGoing = userRsvp?.status === 'going';
  const canAccessChat = isOrganiser || currentUserGoing;

  const fetchEventDetails = useCallback(async () => {
    try {
      const response = await getEvent(eventId);
      setEvent(response.data.event);
      setRsvps(response.data.rsvps);
      const currentUserRsvp = response.data.rsvps.find(
        (rsvpItem) => rsvpItem.user_id === user.id
      );
      setUserRsvp(currentUserRsvp);
    } catch (error) {
      console.error('Error fetching event:', error);
      toast.error('Failed to load ride details');
      navigate('/ride-24');
    } finally {
      setLoading(false);
    }
  }, [eventId, user.id, navigate]);

  const fetchParticipants = useCallback(async () => {
    try {
      const { data } = await getEventParticipants(eventId);
      setParticipants(data);
    } catch (error) {
      // 403 is expected for users who haven't RSVP'd — just leave participants empty.
      if (error.response?.status !== 403) {
        console.error('Error fetching participants:', error);
      }
      setParticipants([]);
    }
  }, [eventId]);

  const fetchTerms = useCallback(async () => {
    try {
      const response = await getTerms();
      setTerms(response.data.terms);
    } catch (error) {
      console.error('Error fetching terms:', error);
    }
  }, []);

  useEffect(() => {
    fetchEventDetails();
    fetchTerms();
  }, [fetchEventDetails, fetchTerms]);

  useEffect(() => {
    if (event) fetchParticipants();
  }, [event, fetchParticipants, userRsvp?.status]);

  const handleRsvpClick = (status) => {
    const restr = event?.gender_restriction;
    if (
      status === 'going' &&
      restr &&
      restr !== 'anyone' &&
      !user.gender
    ) {
      setGenderDialogOpen(true);
      return;
    }
    setPendingRsvpStatus(status);
    setTermsAccepted(false);
    setShowTermsDialog(true);
  };

  const handleSaveGender = async (gender) => {
    setSavingGender(true);
    try {
      await updateMe({ gender });
      if (onUserRefresh) await onUserRefresh();
      setGenderDialogOpen(false);
      toast.success('Gender saved');
      setPendingRsvpStatus('going');
      setTermsAccepted(false);
      setShowTermsDialog(true);
    } catch (error) {
      toast.error(apiError(error, 'Failed to save gender'));
    } finally {
      setSavingGender(false);
    }
  };

  const handleRsvpSubmit = async () => {
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions to continue');
      return;
    }

    setRsvpLoading(true);
    setShowTermsDialog(false);

    try {
      const response = await rsvp({
        event_id: Number(eventId),
        status: pendingRsvpStatus,
        terms_accepted: termsAccepted,
      });

      setUserRsvp(response.data);
      const updatedRsvps = rsvps.filter((item) => item.user_id !== user.id);
      updatedRsvps.push(response.data);
      setRsvps(updatedRsvps);

      toast.success(
        pendingRsvpStatus === 'going'
          ? "Great! You're signed up for this ride"
          : 'Your RSVP has been updated'
      );
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      toast.error(apiError(error, 'Failed to submit RSVP'));
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleCancelEvent = async () => {
    const reason = cancelReason.trim();
    if (!reason) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    setCancelling(true);
    try {
      await cancelEvent(eventId, { reason });
      toast.success('Ride cancelled');
      setCancelDialogOpen(false);
      setCancelReason('');
      fetchEventDetails();
    } catch (error) {
      toast.error(apiError(error, 'Failed to cancel ride'));
    } finally {
      setCancelling(false);
    }
  };

  const handleRemoveParticipant = async () => {
    if (!removingParticipant) return;
    setRemovalWorking(true);
    try {
      await removeEventParticipant(eventId, removingParticipant.user_id);
      toast.success(`${removingParticipant.user_name} removed from ride`);
      setRemovingParticipant(null);
      fetchParticipants();
      fetchEventDetails();
    } catch (error) {
      toast.error(apiError(error, 'Failed to remove rider'));
    } finally {
      setRemovalWorking(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      easy: 'bg-gray-800 text-gray-200 border-gray-700',
      moderate: 'bg-red-950 text-red-300 border-red-900',
      challenging: 'bg-red-900 text-red-200 border-red-800',
      expert: 'bg-red-600 text-white border-red-700',
    };
    return colors[difficulty] || 'bg-gray-800 text-gray-200 border-gray-700';
  };

  const getGoogleMapsUrl = (location) =>
    `https://maps.google.com/?q=${encodeURIComponent(location)}`;

  const goingRsvps = rsvps.filter((rsvp) => rsvp.status === 'going');
  const notGoingRsvps = rsvps.filter((rsvp) => rsvp.status === 'not_going');

  const pageBg = 'min-h-screen bg-gradient-to-br from-black via-gray-900 to-black';

  if (loading) {
    return (
      <div className={pageBg}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-800 rounded w-1/2 mb-6"></div>
            <div className="h-64 bg-gray-800 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className={`${pageBg} flex items-center justify-center`}>
        <Card className="text-center p-8 bg-gray-900/80 border border-gray-800 text-gray-200">
          <CardContent>
            <h2 className="text-2xl font-bold mb-4 text-white">Ride not found</h2>
            <Button
              onClick={() => navigate('/ride-24')}
              className="bg-red-600 hover:bg-red-700 text-white border-0"
            >
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const cardCls = 'bg-gray-900/80 border border-gray-800 shadow-xl text-gray-200';

  return (
    <div className={pageBg}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Top bar: back + organiser/admin actions */}
        <div className="flex items-center justify-between mb-6 gap-3">
          <Button
            onClick={() => navigate('/ride-24')}
            variant="ghost"
            className="text-gray-300 hover:text-white hover:bg-white/5 -ml-2"
            data-testid="back-to-dashboard-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="flex items-center gap-2">
            {isOrganiser && !isCancelled && (
              <>
                <Button
                  onClick={() => navigate(`/ride-24/events/${eventId}/edit`)}
                  variant="outline"
                  className="border-gray-700 bg-transparent text-gray-200 hover:bg-gray-800 hover:text-white"
                  data-testid="organiser-edit-event-button"
                >
                  <Pencil className="w-4 h-4 mr-1.5" />
                  Edit
                </Button>
                <Button
                  onClick={() => setCancelDialogOpen(true)}
                  variant="outline"
                  className="border-red-900 text-red-300 bg-transparent hover:bg-red-950/50 hover:text-red-200"
                  data-testid="organiser-cancel-event-button"
                >
                  <Ban className="w-4 h-4 mr-1.5" />
                  Cancel ride
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Cancelled banner */}
        {isCancelled && (
          <div
            className="mb-6 flex items-start gap-3 rounded-sm border border-red-900 bg-red-950/40 p-4 text-red-200"
            data-testid="event-cancelled-banner"
          >
            <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-heading uppercase tracking-widest text-red-300 mb-1">
                Ride cancelled
                {event.cancelled_by === 'admin' && ' by admin'}
                {event.cancelled_by === 'organiser' && ' by organiser'}
              </div>
              {event.cancel_reason && (
                <p className="text-red-100/90" data-testid="event-cancel-reason">
                  {event.cancel_reason}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Title block */}
        <div className="flex items-start justify-between mb-8 bg-[#09090B]/80 border border-zinc-900 rounded-sm p-5">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.3em] text-red-500 font-heading mb-2">
              Ride details
            </div>
            <h1
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white leading-none mb-2"
              data-testid="event-title"
            >
              {event.title}
            </h1>
            <p className="text-zinc-400">
              Organized by{' '}
              <span className="text-zinc-200 inline-flex items-center gap-1">
                {event.organizer_name}
                {event.organizer_is_verified && <VerifiedBadge size="md" showLabel />}
              </span>
            </p>
          </div>

          <Badge
            className={`${getDifficultyColor(event.difficulty_level)} text-sm ml-4 flex-shrink-0`}
          >
            {event.difficulty_level.charAt(0).toUpperCase() + event.difficulty_level.slice(1)}
          </Badge>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Photos Gallery — swipeable (the only image section) */}
            {event.photos && event.photos.length > 0 && (
              <Card
                className={`${cardCls} p-0 overflow-hidden`}
                data-testid="event-photos-card"
              >
                <Carousel
                  className="w-full"
                  opts={{ align: 'start', loop: event.photos.length > 1 }}
                >
                  <CarouselContent>
                    {event.photos.map((photo, idx) => (
                      <CarouselItem
                        key={photo.path || idx}
                        data-testid={`event-photo-${idx}`}
                      >
                        <div className="aspect-[16/10] bg-black overflow-hidden">
                          <img
                            src={assetUrl(photo.path)}
                            alt={`Ride photo ${idx + 1}`}
                            className="w-full h-full object-cover"
                            draggable="false"
                          />
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {event.photos.length > 1 && (
                    <>
                      <CarouselPrevious
                        className="left-3 bg-black/70 border-zinc-700 text-white hover:bg-red-600 hover:text-white hover:border-red-600"
                        data-testid="event-photos-prev"
                      />
                      <CarouselNext
                        className="right-3 bg-black/70 border-zinc-700 text-white hover:bg-red-600 hover:text-white hover:border-red-600"
                        data-testid="event-photos-next"
                      />
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 pointer-events-none">
                        {event.photos.map((_, i) => (
                          <span key={i} className="w-1.5 h-1.5 rounded-full bg-white/60" />
                        ))}
                      </div>
                    </>
                  )}
                </Carousel>
              </Card>
            )}

            {/* Event Details */}
            <Card className={cardCls} data-testid="event-details-card">
              <CardHeader>
                <CardTitle className="text-white">Event Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-2 text-white">Description</h3>
                  <p className="text-gray-300 leading-relaxed">{event.description}</p>
                </div>

                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 text-red-500 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-200">Date & Time</h4>
                    <p className="text-gray-400">{formatDate(event.date)}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-red-500 mt-1" />
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-200">Start Point</h4>
                      <div className="flex items-center space-x-2">
                        <p className="text-gray-400">{event.start_point}</p>
                        <a
                          href={getGoogleMapsUrl(event.start_point)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-red-400 hover:text-red-300"
                          data-testid="start-point-maps-link"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-red-500 mt-1" />
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-200">End Point</h4>
                      <div className="flex items-center space-x-2">
                        <p className="text-gray-400">{event.end_point}</p>
                        <a
                          href={getGoogleMapsUrl(event.end_point)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-red-400 hover:text-red-300"
                          data-testid="end-point-maps-link"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>

                  {event.google_maps_link && (
                    <div className="flex items-start space-x-3">
                      <MapIcon className="w-5 h-5 text-red-500 mt-1" />
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-200">Full Route</h4>
                        <a
                          href={event.google_maps_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-red-400 hover:text-red-300 underline break-all"
                          data-testid="event-google-maps-link"
                        >
                          Open in Google Maps
                          <ExternalLink className="w-4 h-4 ml-1" />
                        </a>
                      </div>
                    </div>
                  )}

                  {(event.state || event.district) && (
                    <div className="flex items-start space-x-3">
                      <MapPin className="w-5 h-5 text-red-500 mt-1" />
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-200">Region</h4>
                        <p className="text-gray-400" data-testid="event-region">
                          {[event.district, event.state].filter(Boolean).join(', ')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-800">
                  <div className="flex items-center space-x-2">
                    <i
                      className="fa-solid fa-motorcycle text-gray-400 text-lg not-italic"
                      aria-hidden="true"
                    ></i>
                    <div>
                      <h4 className="font-medium text-gray-200">Ride Type</h4>
                      <p className="text-gray-400 capitalize">
                        {event.bike_type === 'any'
                          ? 'Any'
                          : event.bike_type.replace('_', ' ')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Users className="w-5 h-5 text-gray-400" />
                    <div>
                      <h4 className="font-medium text-gray-200">Participants</h4>
                      <p className="text-gray-400">
                        {goingRsvps.length}/{event.max_participants}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Participants (with profile pictures) */}
            <Card className={cardCls} data-testid="participants-card">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-white">
                  <Users className="w-5 h-5" />
                  <span>Riders going ({goingRsvps.length})</span>
                </CardTitle>
                <CardDescription className="text-gray-400">
                  {isOrganiser
                    ? 'Full participant list — as organiser you can remove riders.'
                    : userRsvp
                    ? 'RSVPd riders are visible to everyone attending.'
                    : 'RSVP to see all the riders going.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {participants.length > 0 ? (
                  <div className="space-y-3" data-testid="participants-list">
                    {participants.map((p) => (
                      <div
                        key={p.user_id}
                        className="flex items-center justify-between gap-3 rounded-sm border border-zinc-800 bg-zinc-950/60 px-3 py-2"
                        data-testid={`participant-${p.user_id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-full overflow-hidden bg-red-600/10 ring-2 ring-red-600/40 flex-shrink-0 flex items-center justify-center">
                            {p.picture ? (
                              <img
                                src={p.picture}
                                alt={p.user_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <User className="w-4 h-4 text-red-400" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-gray-100 font-medium truncate">
                              {p.user_name}
                              {p.user_id === event.organizer_id && (
                                <span className="ml-2 text-[10px] font-heading uppercase tracking-widest text-red-400">
                                  Organiser
                                </span>
                              )}
                            </div>
                            {p.gender && (
                              <div className="text-xs text-zinc-500 capitalize">
                                {p.gender}
                              </div>
                            )}
                          </div>
                        </div>

                        {isOrganiser &&
                          !isCancelled &&
                          p.user_id !== event.organizer_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setRemovingParticipant(p)}
                              className="border-red-900 text-red-300 bg-transparent hover:bg-red-950/50 hover:text-red-200"
                              data-testid={`remove-participant-${p.user_id}`}
                            >
                              <UserMinus className="w-3.5 h-3.5 mr-1" />
                              Remove
                            </Button>
                          )}
                      </div>
                    ))}
                  </div>
                ) : goingRsvps.length > 0 && !userRsvp && !isOrganiser ? (
                  <p
                    className="text-gray-500 text-center py-6"
                    data-testid="participants-locked"
                  >
                    {goingRsvps.length} rider{goingRsvps.length === 1 ? '' : 's'} going.
                    RSVP to see who they are.
                  </p>
                ) : (
                  <p className="text-gray-500 text-center py-6">
                    No participants yet. Be the first to join!
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Group chat (only for RSVPd going / organiser / admin) */}
            {canAccessChat ? (
              <EventChat
                eventId={eventId}
                user={user}
                canPost={!isCancelled}
                disabledReason={
                  isCancelled ? 'This ride has been cancelled. Chat is closed.' : null
                }
              />
            ) : (
              <Card
                className={cardCls}
                data-testid="chat-teaser"
              >
                <CardHeader>
                  <CardTitle className="text-white">Group Chat</CardTitle>
                  <CardDescription className="text-gray-400">
                    RSVP as going to join the group chat with the organiser and other riders.
                  </CardDescription>
                </CardHeader>
              </Card>
            )}
          </div>

          {/* Sidebar - RSVP */}
          <div className="lg:col-span-1">
            <Card className={`${cardCls} sticky top-24`} data-testid="rsvp-card">
              <CardHeader>
                <CardTitle className="text-white">RSVP for Ride</CardTitle>
                <CardDescription className="text-gray-400">
                  Let others know if you&apos;re planning to attend
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {userRsvp ? (
                  <div className="text-center p-4 bg-black/40 border border-gray-800 rounded-lg">
                    <div className="flex items-center justify-center mb-2">
                      {userRsvp.status === 'going' ? (
                        <CheckCircle className="w-6 h-6 text-red-500" />
                      ) : (
                        <XCircle className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                    <p className="font-medium text-white">
                      You&apos;re {userRsvp.status === 'going' ? 'going' : 'not going'}
                    </p>
                    {!isCancelled && (
                      <p className="text-sm text-gray-400 mt-1">Want to change your RSVP?</p>
                    )}
                  </div>
                ) : null}

                {isCancelled ? (
                  <div
                    className="text-sm text-red-300 border border-red-900 bg-red-950/40 rounded-sm p-3 text-center"
                    data-testid="rsvp-cancelled-notice"
                  >
                    RSVPs are closed — this ride has been cancelled.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(() => {
                      const restr = event.gender_restriction;
                      const userGender = user.gender;
                      const isRestricted = restr && restr !== 'anyone';
                      const needsGender = isRestricted && !userGender;
                      const genderAllowed =
                        !isRestricted ||
                        (restr === 'male_only' && userGender === 'male') ||
                        (restr === 'female_only' && userGender === 'female');
                      return (
                        <>
                          {isRestricted && (
                            <div
                              className={`text-xs px-3 py-2 rounded-sm border ${
                                restr === 'female_only'
                                  ? 'bg-pink-950/40 border-pink-900 text-pink-200'
                                  : 'bg-sky-950/40 border-sky-900 text-sky-200'
                              }`}
                              data-testid="rsvp-gender-restriction-notice"
                            >
                              {restr === 'female_only'
                                ? '♀ Female-only ride'
                                : '♂ Male-only ride'}
                              {!genderAllowed && (
                                <div className="text-red-300 mt-1">
                                  {userGender
                                    ? 'You cannot RSVP as going.'
                                    : 'Set your gender to RSVP to this ride.'}
                                </div>
                              )}
                            </div>
                          )}
                          <Button
                            onClick={() => handleRsvpClick('going')}
                            disabled={rsvpLoading || (isRestricted && !!userGender && !genderAllowed)}
                            className="w-full btn-hover bg-red-600 hover:bg-red-700 text-white border-0 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="going-rsvp-button"
                            title={
                              isRestricted && userGender && !genderAllowed
                                ? 'This ride is gender-restricted'
                                : undefined
                            }
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {needsGender
                              ? 'Set gender & RSVP'
                              : userRsvp?.status === 'going'
                                ? 'Still Going'
                                : "I'm Going"}
                          </Button>
                          <Button
                            onClick={() => handleRsvpClick('not_going')}
                            disabled={rsvpLoading}
                            variant="outline"
                            className="w-full btn-hover border-gray-700 bg-transparent text-gray-200 hover:bg-gray-800 hover:text-white"
                            data-testid="not-going-rsvp-button"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            {userRsvp?.status === 'not_going'
                              ? 'Still Not Going'
                              : "Can't Make It"}
                          </Button>
                        </>
                      );
                    })()}
                  </div>
                )}

                <Separator className="bg-gray-800" />

                <div className="text-sm text-gray-400 space-y-2">
                  <p>
                    <span className="text-gray-200 font-medium">Going:</span>{' '}
                    {goingRsvps.length} people
                  </p>
                  <p>
                    <span className="text-gray-200 font-medium">Can&apos;t make it:</span>{' '}
                    {notGoingRsvps.length} people
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Terms Dialog */}
      <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
        <DialogContent
          className="max-w-2xl max-h-[80vh] overflow-y-auto bg-gray-900 border border-gray-800 text-gray-200"
          data-testid="terms-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white">Terms and Conditions</DialogTitle>
            <DialogDescription className="text-gray-400">
              Please read and accept the terms and conditions to continue with your RSVP
            </DialogDescription>
          </DialogHeader>

          <div className="bg-black/50 border border-gray-800 p-4 rounded-lg max-h-60 overflow-y-auto">
            <pre className="text-sm text-gray-300 whitespace-pre-wrap font-sans">{terms}</pre>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="terms"
              checked={termsAccepted}
              onCheckedChange={setTermsAccepted}
              className="border-gray-600 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
              data-testid="terms-checkbox"
            />
            <Label htmlFor="terms" className="text-sm text-gray-300">
              I have read and agree to the terms and conditions
            </Label>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowTermsDialog(false)}
              className="border-gray-700 bg-transparent text-gray-200 hover:bg-gray-800 hover:text-white"
              data-testid="terms-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRsvpSubmit}
              disabled={!termsAccepted || rsvpLoading}
              className="bg-red-600 hover:bg-red-700 text-white border-0 disabled:opacity-50"
              data-testid="terms-accept-button"
            >
              {rsvpLoading ? 'Processing...' : 'Accept & RSVP'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gender required for restricted rides */}
      <Dialog open={genderDialogOpen} onOpenChange={setGenderDialogOpen}>
        <DialogContent
          className="max-w-md bg-gray-900 border border-gray-800 text-gray-200"
          data-testid="gender-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white font-heading uppercase tracking-tight">
              Set your gender
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              This ride has a gender restriction. Choose your gender to continue RSVPing.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-2">
            {['male', 'female', 'other'].map((g) => (
              <Button
                key={g}
                disabled={savingGender}
                onClick={() => handleSaveGender(g)}
                className="bg-zinc-800 hover:bg-red-600 text-white capitalize"
                data-testid={`gender-option-${g}`}
              >
                {g}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel event dialog */}
      <Dialog
        open={cancelDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setCancelDialogOpen(false);
            setCancelReason('');
          }
        }}
      >
        <DialogContent
          className="max-w-lg bg-gray-900 border border-gray-800 text-gray-200"
          data-testid="cancel-event-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white font-heading uppercase tracking-tight">
              Cancel this ride?
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              RSVPs will be closed and riders will see the cancellation reason.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="cancel_reason" className="text-gray-200">
              Reason (required)
            </Label>
            <Textarea
              id="cancel_reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={4}
              placeholder="e.g., Bad weather forecast, route closed, safety concerns…"
              className="bg-gray-950 border-gray-700 text-gray-100 placeholder:text-gray-500"
              data-testid="cancel-reason-input"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              className="border-gray-700 bg-transparent text-gray-200 hover:bg-gray-800 hover:text-white"
              data-testid="cancel-event-back-button"
            >
              Back
            </Button>
            <Button
              onClick={handleCancelEvent}
              disabled={!cancelReason.trim() || cancelling}
              className="bg-red-600 hover:bg-red-700 text-white border-0 disabled:opacity-50"
              data-testid="cancel-event-confirm-button"
            >
              {cancelling ? 'Cancelling…' : 'Cancel ride'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove participant dialog */}
      <Dialog
        open={!!removingParticipant}
        onOpenChange={(open) => {
          if (!open) setRemovingParticipant(null);
        }}
      >
        <DialogContent
          className="max-w-md bg-gray-900 border border-gray-800 text-gray-200"
          data-testid="remove-participant-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white font-heading uppercase tracking-tight">
              Remove rider?
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {removingParticipant?.user_name} will be removed from the ride and the
              group chat. They won&apos;t be able to RSVP as going again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRemovingParticipant(null)}
              className="border-gray-700 bg-transparent text-gray-200 hover:bg-gray-800 hover:text-white"
            >
              Back
            </Button>
            <Button
              onClick={handleRemoveParticipant}
              disabled={removalWorking}
              className="bg-red-600 hover:bg-red-700 text-white border-0 disabled:opacity-50"
              data-testid="remove-participant-confirm-button"
            >
              {removalWorking ? 'Removing…' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventDetails;
